import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface LanguageContextType {
  language: string;
  defaultLanguage: string;
  isRTL: boolean;
  changeLanguage: (lang: string) => void;
  setDefaultLanguage: (lang: string) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();

  // Get default language from localStorage or fallback to 'en'
  const getDefaultLanguage = () => {
    return localStorage.getItem('defaultLanguage') || 'en';
  };

  // Initialize with default language if no current language is set
  const initLanguage = () => {
    const storedLang = localStorage.getItem('i18nextLng');
    return storedLang || getDefaultLanguage();
  };

  const [language, setLanguage] = useState(initLanguage());
  const [defaultLanguage, setDefaultLanguageState] = useState(getDefaultLanguage());
  const [isRTL, setIsRTL] = useState(language === 'ar');

  useEffect(() => {
    const updateDirection = (lang: string) => {
      const rtl = lang === 'ar';
      setIsRTL(rtl);
      document.documentElement.dir = rtl ? 'rtl' : 'ltr';
      document.documentElement.lang = lang;
    };

    updateDirection(i18n.language);

    const handleLanguageChange = (lang: string) => {
      setLanguage(lang);
      updateDirection(lang);
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('i18nextLng', lang);
  };

  const setDefaultLanguage = (lang: string) => {
    localStorage.setItem('defaultLanguage', lang);
    setDefaultLanguageState(lang);

    // If no specific language is set, switch to the new default
    const currentStoredLang = localStorage.getItem('i18nextLng');
    if (!currentStoredLang) {
      changeLanguage(lang);
    }
  };

  // Initialize i18n with the correct language on mount
  useEffect(() => {
    const targetLang = initLanguage();
    if (i18n.language !== targetLang) {
      i18n.changeLanguage(targetLang);
    }
  }, [i18n]);

  return (
    <LanguageContext.Provider value={{
      language,
      defaultLanguage,
      isRTL,
      changeLanguage,
      setDefaultLanguage
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
